import type { KeyboardShortcut } from './types';

export const editableHtmlElements = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'CONTENTEDITABLE']);

export const nonTextInputTypes = new Set([
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'range',
  'color',
  'file',
  'image',
]);

export const SHORTCUT_KEYS = {
  // undo / redo
  z: 'z',

  // copy
  c: 'c',

  // paste
  v: 'v',

  // toggle diff mode
  '/': '/',

  // open Node Search
  f: 'f',

  // toggle the AI chat panel
  i: 'i',

  Escape: 'Escape',

  Enter: 'Enter',

  ArrowDown: 'ArrowDown',

  ArrowUp: 'ArrowUp',
} as const;

export type ShortcutKey = keyof typeof SHORTCUT_KEYS;

// ---- Keyboard Shortcut Constants ----

/** Triggers toggling the diff view. */
export const diffViewShortcut: KeyboardShortcut = {
  key: '/',
  primaryModifier: true,
};

// ---- General Use Shortcuts ----

/** Key: Escape. General use dismiss shortcut. */
export const dismissShortcut: KeyboardShortcut = {
  key: 'Escape',
};

/** Key: Enter. General use confirm shortcut. */
export const confirmShortcut: KeyboardShortcut = {
  key: 'Enter',
};

// ---- Node Search Shortcuts ----

/** Key: ArrowDown. Triggers moving the search index to the next search match in node search. */
export const nextShortcut: KeyboardShortcut = {
  key: 'ArrowDown',
};

/** Key: ArrowUp. Triggers moving the search index to the previous search match in node search. */
export const prevShortcut: KeyboardShortcut = {
  key: 'ArrowUp',
};

/** Key: Shift + Enter. Triggers moving the search index to the previous search match in node search. */
export const prevSearchMatchShortcut: KeyboardShortcut = {
  key: 'Enter',
  shiftKey: true,
};

/** Triggers opening the node search box. */
export const nodeSearchShortcut: KeyboardShortcut = {
  key: 'f',
  primaryModifier: true,
};

/** Triggers focusing a node in node search.. */
export const focusSearchMatchShortcut: KeyboardShortcut = {
  key: 'Enter',
  primaryModifier: true,
};

/** Triggers moving the search index to the previous search match in node search. */
export const nodeSearchPrevShortcut: KeyboardShortcut = {
  key: 'ArrowUp',
};

// ---- Workflow Copy / Paste Shortcuts ----

/** Triggers copying the workflow json onto the clipboard. */
export const copyShortcut: KeyboardShortcut = {
  key: 'c',
  primaryModifier: true,
};

/** Triggers pasting the clipboard contents onto a workflow. */
export const pasteShortcut: KeyboardShortcut = {
  key: 'v',
  primaryModifier: true,
};

// ---- Workflow Undo / Redo Shortcuts ----

/** Triggers undoing the last action. */
export const undoShortcut: KeyboardShortcut = {
  key: 'z',
  primaryModifier: true,
};

/** Triggers redoing the last action. */
export const redoShortcut: KeyboardShortcut = {
  key: 'z',
  shiftKey: true,
  primaryModifier: true,
};

// ---- AI Chat Shortcuts ----

/** Toggles the AI chat panel. */
export const aiChatShortcut: KeyboardShortcut = {
  key: 'i',
  primaryModifier: true,
};
